import AsyncStorage from "@react-native-async-storage/async-storage";

const conversationsByJobAndStarter = new Map<string, string>();
const conversationsByJob = new Map<string, string>();
let activeViewjobJobKey: string | null = null;

const FEATURE_VIEW_JOB = "viewjob";

const VIEWJOB_BREADCRUMB_STORAGE_PREFIX = "@careerScout/viewjobBreadcrumb/";

const viewjobBreadcrumbByConversationId = new Map<string, ViewjobBreadcrumb>();

function makeViewjobBreadcrumbStorageKey(conversationId: string): string {
  return `${VIEWJOB_BREADCRUMB_STORAGE_PREFIX}${conversationId}`;
}

function normalizeBreadcrumb(breadcrumb: ViewjobBreadcrumb): ViewjobBreadcrumb {
  return {
    jobKey: breadcrumb.jobKey,
    ...(breadcrumb.jobTitle ? { jobTitle: breadcrumb.jobTitle } : {}),
    ...(breadcrumb.companyName ? { companyName: breadcrumb.companyName } : {}),
  };
}

export type ViewjobSourceContext = {
  feature: typeof FEATURE_VIEW_JOB;
  jobKey: string;
  starter?: string;
  jobTitle?: string;
  companyName?: string;
};

export type ViewjobBreadcrumb = {
  jobKey: string;
  jobTitle?: string;
  companyName?: string;
};

export type ViewjobBreadCrumbRenderData = ViewjobBreadcrumb;

function makeKey(jobKey: string, starter: string): string {
  return `${jobKey}\u0000${starter}`;
}

export function saveConversationForStarter(
  starter: string,
  conversationId: string,
  jobKey: string,
): void {
  if (!starter || !conversationId || !jobKey) {
    return;
  }
  conversationsByJobAndStarter.set(makeKey(jobKey, starter), conversationId);
}

export function getConversationForStarter(starter: string, jobKey: string): string | null {
  if (!starter || !jobKey) {
    return null;
  }
  return conversationsByJobAndStarter.get(makeKey(jobKey, starter)) ?? null;
}

export function saveConversationForViewjob(jobKey: string, conversationId: string): void {
  if (!jobKey || !conversationId) {
    return;
  }
  conversationsByJob.set(jobKey, conversationId);
}

export function getConversationForViewjob(jobKey: string): string | null {
  if (!jobKey) {
    return null;
  }
  return conversationsByJob.get(jobKey) ?? null;
}

export function setActiveViewjobJobKey(jobKey: string): void {
  activeViewjobJobKey = jobKey || null;
}

export function getActiveViewjobJobKey(): string | null {
  return activeViewjobJobKey;
}

type ViewjobSourceContextExtras = {
  jobTitle?: string | null;
  companyName?: string | null;
};

function applyExtras(
  ctx: ViewjobSourceContext,
  extras?: ViewjobSourceContextExtras,
): ViewjobSourceContext {
  if (extras?.jobTitle) {
    ctx.jobTitle = extras.jobTitle;
  }
  if (extras?.companyName) {
    ctx.companyName = extras.companyName;
  }
  return ctx;
}

export function encodeViewjobStarterSourceContext(
  jobKey: string,
  starter: string,
  extras?: ViewjobSourceContextExtras,
): string {
  const ctx: ViewjobSourceContext = applyExtras(
    {
      feature: FEATURE_VIEW_JOB,
      jobKey,
      starter,
    },
    extras,
  );
  return JSON.stringify(ctx);
}

export function encodeViewjobSourceContext(
  jobKey: string,
  extras?: ViewjobSourceContextExtras,
): string {
  const ctx: ViewjobSourceContext = applyExtras(
    {
      feature: FEATURE_VIEW_JOB,
      jobKey,
    },
    extras,
  );
  return JSON.stringify(ctx);
}

export function isViewjobSourceContext(sourceContext?: string | null): boolean {
  if (!sourceContext) {
    return false;
  }
  if (sourceContext === FEATURE_VIEW_JOB) {
    return true;
  }
  return decodeViewjobSourceContext(sourceContext) !== null;
}

export function getViewjobJobKeyFromSourceContext(sourceContext?: string | null): string | null {
  const decoded = decodeViewjobSourceContext(sourceContext);
  if (decoded?.jobKey) {
    return decoded.jobKey;
  }
  if (sourceContext === FEATURE_VIEW_JOB) {
    return getActiveViewjobJobKey();
  }
  return null;
}

export const CONVERSATION_SOURCE_ID_TAG_KEY = "sourceId";

export const VIEWJOB_SOURCE_ID_PREFIX = "viewJob:";
export const SERP_SOURCE_ID_TAG_VALUE = "serp";

export function buildViewjobSourceIdTagValue(jobKey: string): string {
  return `${VIEWJOB_SOURCE_ID_PREFIX}${jobKey}`;
}

export function buildFreeformSourceIdTagValue(_sourceContext: string): string {
  return SERP_SOURCE_ID_TAG_VALUE;
}

export type ViewjobConversationMetadata = {
  tags: Array<{ key: string; value: string }>;
  sourceContext: string;
};

/**
 * Generic Career Scout conversation metadata shape. Callers with a structured
 * `sourceId` (like View Job) include `tags`, while the SERP free-form context
 * maps to the stable `serp` sourceId so repeated launches can key the same
 * conversation.
 */
export type ConversationMetadata = {
  tags?: Array<{ key: string; value: string }>;
  sourceContext: string;
};

export type SourceContextPrefetchEntry = {
  tool: "sourceContext";
  params: {
    sourceContext: string;
    sourceId?: string;
  };
};

export function buildViewjobConversationMetadata(jobKey: string): ViewjobConversationMetadata {
  return {
    tags: [{ key: CONVERSATION_SOURCE_ID_TAG_KEY, value: buildViewjobSourceIdTagValue(jobKey) }],
    sourceContext: `The user is looking at job key ${jobKey}`,
  };
}

export function buildViewjobListThreadsMetadataFilter(
  jobKey: string,
): Pick<ViewjobConversationMetadata, "tags"> {
  const { tags } = buildViewjobConversationMetadata(jobKey);
  return { tags };
}

export type ViewjobThreadMatchCandidate = {
  id: string;
  updatedAt?: Date | string | null;
};

export function findExistingViewjobThreadId(
  candidates: readonly ViewjobThreadMatchCandidate[] | null | undefined,
): string | null {
  if (!candidates || candidates.length === 0) {
    return null;
  }
  let bestId: string | null = null;
  let bestMs = -Infinity;
  for (const candidate of candidates) {
    const updatedMs = candidate.updatedAt == null ? 0 : new Date(candidate.updatedAt).getTime();
    const candidateMs = Number.isFinite(updatedMs) ? updatedMs : 0;
    if (bestId === null || candidateMs > bestMs) {
      bestId = candidate.id;
      bestMs = candidateMs;
    }
  }
  return bestId;
}

export function getViewjobConversationMetadataFromSourceContext(
  sourceContext?: string | null,
): ViewjobConversationMetadata | null {
  const jobKey = getViewjobJobKeyFromSourceContext(sourceContext);
  if (!jobKey) {
    return null;
  }
  return buildViewjobConversationMetadata(jobKey);
}

/**
 * Derives the Career Scout `createMemoryThread` metadata payload from any
 * `sourceContext` value supplied by the entry point (deep link / bridge
 * call).
 *
 * - View Job payloads (JSON `{ feature: 'viewjob', jobKey, ... }` or the
 *   legacy `'viewjob'` literal with an active job key) get the structured
 *   `{ tags, sourceContext }` shape.
 * - Any other non-empty `sourceContext` (e.g. the SERP Career Scout promo's
 *   natural-language sentence) is passed through as `metadata.sourceContext`
 *   and also gets the stable `serp` sourceId tag.
 * - Empty/missing sourceContext returns `null` so `createMemoryThread` is
 *   called without a `metadata` argument.
 */
export function getConversationMetadataFromSourceContext(
  sourceContext?: string | null,
): ConversationMetadata | null {
  const viewjobMetadata = getViewjobConversationMetadataFromSourceContext(sourceContext);
  if (viewjobMetadata) {
    return viewjobMetadata;
  }
  // Avoid forwarding sentinel/structured viewjob payloads as raw text when
  // we couldn't recover a jobKey (e.g. the legacy 'viewjob' literal with no
  // active jobKey, or a malformed viewjob JSON). Those are routing hints,
  // not agent context.
  if (isViewjobSourceContext(sourceContext)) {
    return null;
  }
  const trimmed = sourceContext?.trim();
  if (!trimmed) {
    return null;
  }
  return {
    tags: [
      {
        key: CONVERSATION_SOURCE_ID_TAG_KEY,
        value: buildFreeformSourceIdTagValue(trimmed),
      },
    ],
    sourceContext: trimmed,
  };
}

export function getSourceContextPrefetchEntry(
  sourceContext?: string | null,
): SourceContextPrefetchEntry | null {
  const viewjobMetadata = getViewjobConversationMetadataFromSourceContext(sourceContext);
  if (viewjobMetadata) {
    const jobKey = getViewjobJobKeyFromSourceContext(sourceContext);
    if (!jobKey) {
      return null;
    }
    return {
      tool: "sourceContext",
      params: {
        sourceId: buildViewjobSourceIdTagValue(jobKey),
        sourceContext: viewjobMetadata.sourceContext,
      },
    };
  }

  if (isViewjobSourceContext(sourceContext)) {
    return null;
  }

  const trimmed = sourceContext?.trim();
  if (!trimmed) {
    return null;
  }

  return {
    tool: "sourceContext",
    params: {
      sourceId: buildFreeformSourceIdTagValue(trimmed),
      sourceContext: trimmed,
    },
  };
}

export function decodeViewjobSourceContext(
  sourceContext?: string | null,
): ViewjobSourceContext | null {
  if (!sourceContext || sourceContext === FEATURE_VIEW_JOB) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(sourceContext);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      (parsed as { feature?: unknown }).feature === FEATURE_VIEW_JOB &&
      typeof (parsed as { jobKey?: unknown }).jobKey === "string" &&
      ((parsed as { starter?: unknown }).starter === undefined ||
        typeof (parsed as { starter?: unknown }).starter === "string") &&
      ((parsed as { jobTitle?: unknown }).jobTitle === undefined ||
        typeof (parsed as { jobTitle?: unknown }).jobTitle === "string") &&
      ((parsed as { companyName?: unknown }).companyName === undefined ||
        typeof (parsed as { companyName?: unknown }).companyName === "string")
    ) {
      return parsed as ViewjobSourceContext;
    }
  } catch {
    return null;
  }
  return null;
}

export function getViewjobBreadcrumbFromSourceContext(
  sourceContext?: string | null,
): ViewjobBreadcrumb | null {
  const decoded = decodeViewjobSourceContext(sourceContext);
  if (decoded?.jobKey) {
    return {
      jobKey: decoded.jobKey,
      jobTitle: decoded.jobTitle,
      companyName: decoded.companyName,
    };
  }
  if (sourceContext === FEATURE_VIEW_JOB) {
    const jobKey = getActiveViewjobJobKey();
    if (jobKey) {
      return { jobKey };
    }
  }
  return null;
}

export function mergeViewjobBreadcrumbWithRenderData(
  baseline: ViewjobBreadcrumb | null | undefined,
  renderData: ViewjobBreadCrumbRenderData | null | undefined,
): ViewjobBreadcrumb | null {
  const jobKey = renderData?.jobKey || baseline?.jobKey;
  if (!jobKey) {
    return null;
  }
  return normalizeBreadcrumb({
    jobKey,
    jobTitle: renderData?.jobTitle || baseline?.jobTitle,
    companyName: renderData?.companyName || baseline?.companyName,
  });
}

export function decodeViewjobStarterSourceContext(
  sourceContext?: string | null,
): (ViewjobSourceContext & { starter: string }) | null {
  const parsed = decodeViewjobSourceContext(sourceContext);
  if (!parsed?.starter) {
    return null;
  }
  return parsed as ViewjobSourceContext & { starter: string };
}

export async function saveViewjobBreadcrumbForConversation(
  conversationId: string,
  breadcrumb: ViewjobBreadcrumb,
): Promise<void> {
  if (!conversationId || !breadcrumb.jobKey) {
    return;
  }
  const normalized = normalizeBreadcrumb(breadcrumb);
  viewjobBreadcrumbByConversationId.set(conversationId, normalized);
  try {
    await AsyncStorage.setItem(
      makeViewjobBreadcrumbStorageKey(conversationId),
      JSON.stringify(normalized),
    );
  } catch {
    // best-effort cache; AsyncStorage failure is not user-actionable.
  }
}

export function getCachedViewjobBreadcrumbForConversation(
  conversationId?: string | null,
): ViewjobBreadcrumb | null {
  if (!conversationId) {
    return null;
  }
  return viewjobBreadcrumbByConversationId.get(conversationId) ?? null;
}

export async function loadViewjobBreadcrumbForConversation(
  conversationId?: string | null,
): Promise<ViewjobBreadcrumb | null> {
  if (!conversationId) {
    return null;
  }
  const cached = viewjobBreadcrumbByConversationId.get(conversationId);
  if (cached) {
    return cached;
  }
  try {
    const raw = await AsyncStorage.getItem(makeViewjobBreadcrumbStorageKey(conversationId));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      typeof (parsed as { jobKey?: unknown }).jobKey === "string" &&
      ((parsed as { jobTitle?: unknown }).jobTitle === undefined ||
        typeof (parsed as { jobTitle?: unknown }).jobTitle === "string") &&
      ((parsed as { companyName?: unknown }).companyName === undefined ||
        typeof (parsed as { companyName?: unknown }).companyName === "string")
    ) {
      const hydrated = normalizeBreadcrumb(parsed as ViewjobBreadcrumb);
      viewjobBreadcrumbByConversationId.set(conversationId, hydrated);
      return hydrated;
    }
  } catch {
    // Treat corrupted/JSON-parse errors the same as a cache miss.
  }
  return null;
}

export function clearAllConversationStarters(): void {
  conversationsByJobAndStarter.clear();
  conversationsByJob.clear();
  viewjobBreadcrumbByConversationId.clear();
  activeViewjobJobKey = null;
}
